import moment from "moment";
import {
    getWorkspacePlanLimits,
    canReceiveExpertConsultation
} from "../../../clientServices/workspaceLimits";
import { usePaidPlanSubscription } from "../hooks/usePaidPlanSubscription";
import {
    getIconForWorkspacePlan,
    getTitleForWorkspacePlan,
    getDescriptionForWorkspacePlan,
    getSupportForWorkspacePlan
} from "../../routes/workspace/utils";
import { getIncludedCredits, getPricePerCredit } from "../../utils/ubpUtils";
import globalStore from "../../../state";
import { useStore } from "zustand";
import { Plan } from "@functions/plans/types";

export const usePlanInfo = () => {
    const usage = useStore(globalStore, (state) => state.getUsage());
    const { subscription } = usePaidPlanSubscription();

    const starterPlanLimits = getWorkspacePlanLimits(Plan.LegacyStarter);

    let isTrial = usage?.trial?.isTrial;

    return {
        getExpertConsultation: () => canReceiveExpertConsultation(window.currentWorkspace),
        [Plan.LegacyStarter]: {
            title: isTrial ? "Starter Trial" : "Starter Plan",
            icon: getIconForWorkspacePlan(Plan.LegacyStarter),
            description: getDescriptionForWorkspacePlan(Plan.LegacyStarter),
            features: [
                {
                    title: "What's included",
                    items: [
                        { icon: "far fa-lock", boldText: "Private", text: "data" },
                        { boldText: starterPlanLimits.projects.toLocaleString(), text: "projects" },
                        {
                            boldText: starterPlanLimits.sources.toLocaleString(),
                            text: "base source images"
                        },
                        {
                            boldText: starterPlanLimits.trainingQuota.toLocaleString(),
                            text: `training credits + ${starterPlanLimits.trainingReloads.toLocaleString()}/${
                                subscription?.isAnnual ? "yr" : "mo"
                            }`
                        },
                        {
                            boldText: starterPlanLimits.autoLabelCredits.toLocaleString(),
                            text: `Auto Label credits/${
                                subscription?.isAnnual ? "yr" : "mo"
                            } + discounted additional credits`
                        }
                    ]
                },
                {
                    title: "Key features",
                    items: [
                        { text: "Priority Support" },
                        { text: "Active Learning" },
                        { text: "Automated & Outsourced Labeling" },
                        { text: "Accurate Train" },
                        { text: "Model Evaluation" },
                        { text: "Dataset Analytics" },
                        { text: "Premium Preprocessing & Augmentations" }
                    ]
                }
            ]
        },
        [Plan.UbpBasic]: {
            title: isTrial ? "Basic Trial" : "Basic Plan",
            icon: getIconForWorkspacePlan(Plan.UbpBasic),
            description: getDescriptionForWorkspacePlan(Plan.UbpBasic)
        },
        [Plan.UbpCore]: {
            title: isTrial ? "Core Trial" : "Core Plan",
            icon: getIconForWorkspacePlan(Plan.UbpCore),
            description: getDescriptionForWorkspacePlan(Plan.UbpCore)
        },
        [Plan.UbpGrowth]: {
            title: isTrial ? "Premium Trial" : "Growth Plan",
            icon: getIconForWorkspacePlan(Plan.UbpGrowth),
            description: getDescriptionForWorkspacePlan(Plan.UbpGrowth)
        },
        [Plan.UbpPremiumTrial]: {
            title: "Premium Trial",
            icon: getIconForWorkspacePlan(Plan.UbpPremiumTrial),
            description: getDescriptionForWorkspacePlan(Plan.UbpPremiumTrial)
        },
        expiring: {
            // Bundle/tier downgrades (has tierCredits) show a different message than plan cancellations.
            title: (() => {
                const dp = usage?.downgradePlan;
                const isBundleDowngrade = !!dp && typeof dp === "object" && dp.tierCredits != null;
                const dateStr = subscription?.cancelAt
                    ? moment(new Date(subscription.cancelAt * 1000)).format("LL")
                    : "";
                return isBundleDowngrade
                    ? `Your Core Plan will include fewer credits starting on ${dateStr}.`
                    : `Your Paid Plan will expire on ${dateStr}.`;
            })(),
            icon: "far fa-heart-broken"
        },
        [Plan.Enterprise]: {
            title: "Enterprise Plan",
            icon: getIconForWorkspacePlan(Plan.Enterprise),
            description: getDescriptionForWorkspacePlan(Plan.Enterprise),
            features: [
                {
                    title: "What's included",
                    items: [
                        { icon: "far fa-lock", boldText: "Private", text: "data" },
                        { boldText: "Custom", text: "limits" },
                        { boldText: "Custom", text: "contracting" },
                        { text: "Dedicated engineers and infrastructure" },
                        { text: "Managed solutions" }
                    ]
                },
                {
                    title: "Key features",
                    items: [
                        { text: "Everything in the Starter Plan" },
                        { text: "Single Sign-On (SSO)" },
                        { text: "Active Directory Integration" },
                        { text: "Project Groups" },
                        { text: "Deployment Management" },
                        { text: "Offline Deployment" }
                    ]
                }
            ]
        },
        [Plan.Public]: {
            title: "Public Plan",
            icon: getIconForWorkspacePlan(Plan.Public),
            description: getDescriptionForWorkspacePlan(Plan.Public),
            showResearch: true,
            showUsageLink: true
        },
        [Plan.LegacySandbox]: {
            title: "Sandbox Plan",
            icon: getIconForWorkspacePlan(Plan.LegacySandbox)
        },
        [Plan.Research]: { title: "Research Plan", icon: getIconForWorkspacePlan(Plan.Research) }
    };
};

export const getCreditsCycleText = (
    usage: any,
    subscription: { isAnnual?: boolean } | null | undefined
): string => {
    const enterpriseContract = usage?.activeEnterpriseContract;
    if (enterpriseContract) {
        const start = moment(enterpriseContract.currentPeriodStart.seconds * 1000).format("ll");
        const end = moment(enterpriseContract.currentPeriodEnd.seconds * 1000).format("ll");
        return `Current billing cycle (${start} – ${end})`;
    }
    if (usage?.trial?.isTrial) return "";
    return subscription?.isAnnual ? "per year" : "per month";
};

export const useUsageInfo = () => {
    const usage = useStore(globalStore, (state) => state.getUsage());
    const { subscription } = usePaidPlanSubscription();

    const plan = usage?.plan;

    return {
        getExpertConsultation: () => canReceiveExpertConsultation(window.currentWorkspace),
        ubpPlan: {
            title: getTitleForWorkspacePlan(plan, usage?.trial?.isTrial),
            description: getDescriptionForWorkspacePlan(plan),
            icon: getIconForWorkspacePlan(plan),
            features: [
                {
                    title: "What's included",
                    items: [
                        {
                            icon: "far fa-coins",
                            boldText: usage
                                ? `${getIncludedCredits({ usage })?.toLocaleString()} credits`
                                : "",
                            text: getCreditsCycleText(usage, subscription)
                        },
                        usage?.features?.allowPrivateProjects
                            ? { icon: "far fa-lock", boldText: "Private", text: "data" }
                            : { icon: "far fa-globe", boldText: "Public", text: "data" },
                        usage?.trial?.isTrial
                            ? { icon: "far fa-project-diagram", text: "Model Eval and Monitoring" }
                            : {
                                  icon: "far fa-coins",
                                  boldText: usage
                                      ? `$${getPricePerCredit({ usage })?.toLocaleString()}`
                                      : "",
                                  text: "per additional credit"
                              },

                        { icon: "far fa-headset", text: getSupportForWorkspacePlan(plan) }
                    ]
                }
            ]
        }
    };
};
